import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const WTK1Prelude2Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV847/bwv847a/bwv847a-a4.pdf';
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 10, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 10, 50));
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Prelude in C Minor BWV 847.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => window.print();
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-10102">
      <Navbar data-id="element-10103" />
      <div className="pt-20" data-id="element-10104">
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-10105">
          <img src="https://images.unsplash.com/photo-1465847899084-d164df4dedc6?w=1200&h=400&fit=crop" alt="Prelude in C Minor" className="w-full h-64 md:h-80 object-cover" data-id="element-10106" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-10107" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-10108">
            <div className="max-w-7xl mx-auto" data-id="element-10109">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-10110">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-10111" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-10112">
                Prelude in C Minor
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-10113">
                BWV 847 · The Well-Tempered Clavier, Book I
              </p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-10114">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-10115">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-10116">
          <div className="flex items-center space-x-2" data-id="element-10117">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-10118">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-10119" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10120">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-10121">
                  <ZoomInIcon className="h-5 w-5" data-id="element-10122" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-10123">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-10124">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-10125" />
              <span className="hidden sm:inline" data-id="element-10126">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-10127">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-10128" />
              <span className="hidden sm:inline" data-id="element-10129">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-10130">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-10131">
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-10132">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Prelude in C Minor Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-10133" />
          </div>
        </div>
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-10134">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10135">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-10136">
            <div data-id="element-10137">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10138">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-10139">
                <div data-id="element-10140">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10141">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10142">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-10143">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10144">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10145">
                    BWV 847
                  </dd>
                </div>
                <div data-id="element-10146">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10147">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10148">
                    C Minor
                  </dd>
                </div>
                <div data-id="element-10149">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10150">
                    Collection
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10151">
                    The Well-Tempered Clavier, Book I
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-10152">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10153">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10154">
                The Prelude in C Minor from the Well-Tempered Clavier, Book I,
                presents a dramatic contrast to the serene C Major opener. Its
                urgent, propulsive character and rich harmonic language make it
                one of the most expressive preludes in the collection. The piece
                demands careful attention to dynamic shaping and rhythmic
                precision, as the driving sixteenth-note figures must maintain
                clarity while conveying the emotional intensity of the minor
                key.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-10155" />
    </div>;
};